"use client";
// ===================================================================
// מסנן המשפחות — הקריאה שמאחורי שורת המשפחות (המשך DEC-0362).
//
// משפחה היא **סוג** האפליקציה, ולא תחום העבודה שלה: מאגר · ספרייה ·
// ישות · אפליקציה · הגדרות · סוכנים · תוכנת צד שלישי · קבצים · כלי עזר.
// זה ציר שני ובלתי תלוי בפלטפורמה — אפליקציית CRM יכולה להיות מאגר,
// וגם כלי עזר יכול לשבת ב-CRM. לכן שני הבוררים חיים זה לצד זה ואינם
// יורשים זה מזה. השמות הם של גל, מילה במילה, והם ניתנים לעריכה
// מ-/dashboard/entities/types כי הוא ביקש לשלוט בהם בעצמו.
//
// אותם שלושה כללים שנועלים את בורר הפלטפורמות נועלים גם כאן:
//
//   FAIL OPEN — מסלול שלא נפתר לאפליקציה, או אפליקציה שאיש לא שייך
//   למשפחה, נשארים גלויים. רק מסלול שנפתר לאפליקציה ששויכה למשפחות
//   אחרות מוסתר. עד 2026-09-06 אפס אפליקציות היו משויכות, ולכן הבורר לא
//   הסתיר דבר; באותו יום גל סיווג את כל 97 האפליקציות (95 בבת אחת ועוד
//   שתיים בהכרעה נפרדת), ומאז הבורר מסנן בפועל. מי שיוסיף App חדש בלי
//   שיוך יראה אותו בכל משפחה — זה ה-fail-open עובד, לא תקלה.
//
//   מונה ריק הוא null ולא אפס — מספר שגוי גרוע ממספר חסר.
//
//   ARCHIVE הוא בחירה, לא סינון-שקט. בחירה בו מראה בדיוק את מה שנסגר;
//   אי-בחירה בו **אינה מסתירה** את הארכיון משאר התצוגות. הסתרה שבשתיקה
//   הופכת עמוד חי לבלתי-נגיש דרך התפריט, וזה בדיוק מה ש-Rule 41 אוסר.
//   אם גל ירצה שהארכיון ייעלם כברירת מחדל — זו החלטה שלו ושורה אחת.
// ===================================================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabaseClient";
import { resolveRoute } from "@/lib/platforms/usePlatformFilter";

/** מפתח localStorage. יושב לצד "cc-platform-filter". */
export const FAMILY_KEY = "cc-family-filter";

/** סמן "בלי סינון". לעולם אינו slug של משפחה. */
export const ALL_FAMILIES = "all";

/** סמן "רק מה שאיש לא סיווג". מחושב מהיעדר שיוך, ואינו שורה במסד. */
export const UNTAGGED_FAMILY = "untagged";

/**
 * סמן הארכיון. נגזר מ-pm_apps.status = 'ארכיון' ואינו שורת משפחה:
 * "נסגר" הוא מימד אחר מ"מאיזה סוג", ומימד חדש דורש שדה חדש (Rule 36 §3).
 */
export const ARCHIVED = "archived";

export interface AppFamily {
  id: string;
  slug: string;
  title: string;
  /** כמה אפליקציות שויכו למשפחה. null = המצרף אינו זמין בסביבה הזאת. */
  appCount: number | null;
}

interface FamilyCountRow {
  scope: "family" | "all" | "untagged" | "archive";
  family_id: string | null;
  app_count: number;
  routed_app_count: number;
}

/** השבבים שאינם משפחה ובכל זאת נושאים מספר. */
export interface FamilyTotals {
  all: number | null;
  untagged: number | null;
  archived: number | null;
}

interface NavFamilyRow {
  route: string;
  app_id: string;
  app_title: string;
  family_slugs: string[];
  is_archived: boolean;
}

async function fetchFamilies(): Promise<{ families: AppFamily[]; totals: FamilyTotals }> {
  const { data, error } = await supabase.rpc("get_family_registry");
  if (error) throw error;
  const rows = (data ?? []) as Omit<AppFamily, "appCount">[];

  const { data: countData } = await supabase.rpc("get_family_app_counts");
  const countRows = (countData ?? []) as FamilyCountRow[];
  const countsAvailable = countRows.length > 0;

  const perFamily = new Map(
    countRows
      .filter((r) => r.scope === "family" && r.family_id)
      .map((r) => [r.family_id as string, Number(r.app_count)]),
  );
  const scopeCount = (scope: FamilyCountRow["scope"]) =>
    countsAvailable ? Number(countRows.find((r) => r.scope === scope)?.app_count ?? 0) : null;

  return {
    families: rows.map((f) => ({ ...f, appCount: countsAvailable ? (perFamily.get(f.id) ?? 0) : null })),
    totals: {
      all: scopeCount("all"),
      untagged: scopeCount("untagged"),
      archived: scopeCount("archive"),
    },
  };
}

async function fetchNavMap(): Promise<NavFamilyRow[]> {
  const { data, error } = await supabase.rpc("get_nav_family_map");
  if (error) throw error;
  return (data ?? []) as NavFamilyRow[];
}

function loadSelected(): string {
  if (typeof window === "undefined") return ALL_FAMILIES;
  try {
    return localStorage.getItem(FAMILY_KEY) || ALL_FAMILIES;
  } catch {
    return ALL_FAMILIES;
  }
}

export interface FamilyFilter {
  families: AppFamily[];
  selected: string;
  select: (slug: string) => void;
  isFiltering: boolean;
  matches: (href: string) => boolean;
  slugsFor: (href: string) => string[];
  totals: FamilyTotals;
}

export function useFamilyFilter(): FamilyFilter {
  const [selected, setSelected] = useState<string>(ALL_FAMILIES);

  // קריאה אחרי mount: localStorage אינו קיים ב-SSR, וקריאה בתוך
  // ה-initialiser של useState הייתה מפצלת את הרינדור של השרת והלקוח.
  useEffect(() => setSelected(loadSelected()), []);

  const { data: registry } = useQuery({
    queryKey: ["family-registry"],
    queryFn: fetchFamilies,
    staleTime: 5 * 60_000,
  });

  const { data: navMap } = useQuery({
    queryKey: ["nav-family-map"],
    queryFn: fetchNavMap,
    staleTime: 60_000,
  });

  const select = useCallback((slug: string) => {
    setSelected(slug);
    try {
      localStorage.setItem(FAMILY_KEY, slug);
    } catch {
      // אי-שמירה היא נוחות שאבדה, לא תקלה שראוי להציג.
    }
  }, []);

  const { routeSet, slugsByRoute, archivedRoutes } = useMemo(() => {
    const set = new Set<string>();
    const byRoute = new Map<string, string[]>();
    const archived = new Set<string>();
    for (const row of navMap ?? []) {
      set.add(row.route);
      byRoute.set(row.route, row.family_slugs ?? []);
      if (row.is_archived) archived.add(row.route);
    }
    return { routeSet: set, slugsByRoute: byRoute, archivedRoutes: archived };
  }, [navMap]);

  // אותו כלל פתרון בדיוק שמשמש את הפלטפורמות — קידומת ארוכה ביותר.
  // מיובא ולא משוכפל, כדי ששני הצירים לא ייפרדו בשקט (Rule 10).
  const resolve = useCallback((href: string) => resolveRoute(href, routeSet), [routeSet]);

  const slugsFor = useCallback(
    (href: string) => {
      const route = resolve(href);
      return route ? slugsByRoute.get(route) ?? [] : [];
    },
    [resolve, slugsByRoute],
  );

  const isFiltering = selected !== ALL_FAMILIES && routeSet.size > 0;

  const matches = useCallback(
    (href: string) => {
      if (!isFiltering) return true;
      if (selected === ARCHIVED) {
        const route = resolve(href);
        return route ? archivedRoutes.has(route) : false;
      }
      const slugs = slugsFor(href);
      if (selected === UNTAGGED_FAMILY) return slugs.length === 0;
      if (slugs.length === 0) return true;
      return slugs.includes(selected);
    },
    [isFiltering, selected, slugsFor, resolve, archivedRoutes],
  );

  return {
    families: registry?.families ?? [],
    selected,
    select,
    isFiltering,
    matches,
    slugsFor,
    totals: registry?.totals ?? { all: null, untagged: null, archived: null },
  };
}
